import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Tabs,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedChip as Chip, LocalizedTab as Tab, LocalizedTextField as TextField, LocalizedTooltip as Tooltip } from './i18n/LocalizedMui';
import Typography from './i18n/LocalizedTypography';
import { Add, AutoAwesome, Remove } from '@mui/icons-material';
import LalKitabChart from './lalKitab/LalKitabChart';
import i18n from '../i18n';
import { translateInline } from '../i18n/inlineTranslations';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

interface LalKitabSubtabsProps {
  chartId: string;
  varshChartId?: string;
  dob?: string;
  authToken?: string;
  initialReport?: any;
  isHindi?: boolean;
}

const currentLanguage = () => i18n.resolvedLanguage || i18n.language;
const labelText = (_isHindi: boolean | undefined, en: string, hi: string) => translateInline(currentLanguage(), en, hi);

const PLANET_HI: Record<string, string> = {
  Sun: 'सूर्य',
  Moon: 'चंद्र',
  Mars: 'मंगल',
  Mercury: 'बुध',
  Jupiter: 'गुरु',
  Venus: 'शुक्र',
  Saturn: 'शनि',
  Rahu: 'राहु',
  Ketu: 'केतु'
};

const STATUS_HI: Record<string, string> = {
  ALLOWED: 'अनुमति',
  CAUTION: 'सावधानी',
  'Not Recommended': 'अनुशंसित नहीं',
  Unknown: 'अज्ञात',
  supportive: 'सहायक',
  challenging: 'चुनौतीपूर्ण',
  mixed: 'मिश्रित',
  uncertain: 'अनिश्चित',
  saturn_dependent: 'शनि-आधारित',
  none: 'नहीं',
  High: 'उच्च',
  Medium: 'मध्यम',
  Low: 'कम'
};

const TEVA_TYPE_HI: Record<string, string> = {
  '8th House Takkar': 'आठवें भाव की टक्कर',
  '10th House Enemy Conflict': 'दसवें भाव का शत्रु संघर्ष',
  'Mars-like influence': 'मंगल जैसा प्रभाव',
  'Rahu-like influence': 'राहु जैसा प्रभाव',
  'Guru-Chandal type influence': 'गुरु-चांडाल जैसा प्रभाव',
  'Desire and glamour amplification': 'इच्छा और आकर्षण में वृद्धि',
  'Technical-aggressive logic': 'तकनीकी और तीखी बुद्धि'
};

const TEVA_LABEL_HI: Record<string, string> = {
  'Kismat Ka Grah': 'किस्मत का ग्रह',
  'Pakka Ghar': 'पक्का घर',
  Strong: 'मजबूत',
  Weak: 'कमजोर',
  'Soya Hua Grah': 'सोया हुआ ग्रह',
  'Takkar Grah': 'टक्कर ग्रह',
  'Manda / Disturbed': 'मंदा / अशांत',
  Normal: 'सामान्य'
};

const TEVA_NOTE_HI: Record<string, string> = {
  'Strong candidate for luck and destiny support.': 'भाग्य और सहयोग के लिए मजबूत संकेतक।',
  'Placed in Lal Kitab fixed/strong house.': 'लाल किताब के स्थिर/मजबूत भाव में स्थित।',
  'Exalted sign.': 'उच्च राशि में स्थित।',
  'Debilitated sign.': 'नीच राशि में स्थित।',
  'May not give full results naturally.': 'स्वाभाविक रूप से पूर्ण फल देने में देर या कमी दिखा सकता है।',
  'Involved in Lal Kitab conflict/takkar.': 'लाल किताब की टक्कर/संघर्ष स्थिति में शामिल।',
  'No major Lal Kitab condition detected.': 'कोई प्रमुख लाल किताब स्थिति नहीं मिली।'
};

const AREA_HI: Record<string, string> = {
  father: 'पिता',
  authority: 'अधिकार',
  soul: 'आत्मबल',
  government: 'सरकार',
  confidence: 'आत्मविश्वास',
  mother: 'माता',
  mind: 'मन',
  emotions: 'भावनाएं',
  peace: 'शांति',
  home: 'घर',
  courage: 'साहस',
  anger: 'क्रोध',
  property: 'संपत्ति',
  brothers: 'भाई',
  energy: 'ऊर्जा',
  speech: 'वाणी',
  business: 'व्यापार',
  logic: 'तर्क',
  education: 'शिक्षा',
  communication: 'संचार',
  wisdom: 'ज्ञान',
  guru: 'गुरु',
  children: 'संतान',
  luck: 'भाग्य',
  dharma: 'धर्म',
  marriage: 'विवाह',
  comfort: 'सुख',
  luxury: 'सुविधा',
  wife: 'जीवनसाथी',
  relationship: 'संबंध',
  karma: 'कर्म',
  delay: 'देरी',
  discipline: 'अनुशासन',
  work: 'काम',
  struggle: 'संघर्ष',
  foreign: 'विदेश',
  illusion: 'भ्रम',
  'sudden rise': 'अचानक उन्नति',
  desire: 'इच्छा',
  'unconventional path': 'अलग राह',
  detachment: 'वैराग्य',
  spirituality: 'आध्यात्मिकता',
  'past karma': 'पूर्व कर्म',
  separation: 'अलगाव',
  research: 'शोध'
};

const RIN_REASON_HI: Record<string, string> = {
  pitru_rin: 'शुक्र, बुध या राहु का संबंध दूसरे, पांचवें, नौवें या बारहवें भाव से दिख रहा है।',
  matru_rin: 'केतु का संबंध चौथे भाव से दिख रहा है।',
  stree_rin: 'सूर्य, चंद्र या राहु का संबंध दूसरे या सातवें भाव से दिख रहा है।',
  bhratru_rin: 'बुध या शुक्र का संबंध पहले या आठवें भाव से दिख रहा है।',
  bahin_putri_rin: 'बुध का संबंध तीसरे या छठे भाव से दिख रहा है।',
  nirdayi_rin: 'सूर्य, चंद्र या मंगल का संबंध दसवें या बारहवें भाव से दिख रहा है।',
  ajanma_rin: 'सूर्य, शुक्र या मंगल का संबंध बारहवें भाव से दिख रहा है।',
  swa_rin: 'शुक्र, शनि, राहु या केतु का संबंध पांचवें भाव से दिख रहा है।',
  daivi_rin: 'चंद्र या मंगल का संबंध छठे भाव से दिख रहा है।'
};

const RIN_INTERPRETATION_HI: Record<string, string> = {
  pitru_rin: 'यह पिता पक्ष, पूर्वजों, परिवार के आशीर्वाद या भाग्य से जुड़ा कर्म संकेत दिखा सकता है।',
  matru_rin: 'यह माता, घर, मन की शांति या संपत्ति से जुड़ा कर्म संकेत दिखा सकता है।',
  stree_rin: 'यह जीवनसाथी, स्त्रियों, विवाह या परिवार की खुशी से जुड़े संबंध कर्म को दिखा सकता है।',
  bhratru_rin: 'यह भाई-बहन, रिश्तेदारों या करीबी सहयोग प्रणाली में संतुलन की जरूरत दिखा सकता है।',
  bahin_putri_rin: 'यह बहन, बेटी, कन्याओं या बोलचाल के व्यवहार से जुड़ा कर्म संकेत दिखा सकता है।',
  nirdayi_rin: 'यह क्रोध, कठोरता, अहंकार या शक्ति के गलत उपयोग से जुड़ा कर्म दबाव दिखा सकता है।',
  ajanma_rin: 'यह छिपे हुए कर्म, खर्च, नींद, अकेलेपन या निजी जीवन से जुड़े पैटर्न दिखा सकता है।',
  swa_rin: 'यह स्वयं के कर्म, बच्चों, शिक्षा, निर्णय या बुद्धि के उपयोग से जुड़ा संकेत दिखा सकता है।',
  daivi_rin: 'यह आध्यात्मिक संतुलन, ऋण, शत्रु, क्रोध या सेवा से जुड़ा संकेत दिखा सकता है।'
};

const RIN_GUIDANCE_HI: Record<string, string> = {
  pitru_rin: 'पिता, बुजुर्गों और परिवार की परंपरा का सम्मान करें। झूठे वादे और अहंकार से बचें।',
  matru_rin: 'माता और मातृ पक्ष का सम्मान करें। घर में शांति और जिम्मेदारी बनाए रखें।',
  stree_rin: 'जीवनसाथी और स्त्रियों का सम्मान करें। कटु शब्द, अपमान और धोखे से बचें।',
  bhratru_rin: 'परिवार के समझौतों में निष्पक्ष रहें। चालाकी या स्वार्थी व्यवहार से बचें।',
  bahin_putri_rin: 'बहन, बेटी और कन्याओं का सम्मान करें। बोलचाल में सच्चाई और विनम्रता रखें।',
  nirdayi_rin: 'क्रूरता, बदले की भावना और अनावश्यक क्रोध से बचें। शक्ति का जिम्मेदारी से उपयोग करें।',
  ajanma_rin: 'फिजूलखर्ची और छिपे गलत कार्यों से बचें। निजी जीवन को साफ और संतुलित रखें।',
  swa_rin: 'अहंकार, गलत आदतों और बुद्धि के गलत उपयोग से बचें।',
  daivi_rin: 'बदला, भावनात्मक लड़ाई और शक्ति के गलत उपयोग से बचें। विनम्र सेवा करें।'
};

const planetLabel = (isHindi: boolean | undefined, planet: string) => (isHindi ? PLANET_HI[planet] || planet : translateInline(currentLanguage(), planet));
const statusLabel = (isHindi: boolean | undefined, status?: string) => (isHindi ? STATUS_HI[status || 'Unknown'] || status || 'अज्ञात' : translateInline(currentLanguage(), status || 'Unknown'));
const tevaTypeLabel = (isHindi: boolean | undefined, value?: string) => (isHindi ? TEVA_TYPE_HI[value || ''] || value || '' : translateInline(currentLanguage(), value || ''));
const tevaLabel = (isHindi: boolean | undefined, value: string) => (isHindi ? TEVA_LABEL_HI[value] || value : translateInline(currentLanguage(), value));
const replacePlanetNames = (value: string) => Object.entries(PLANET_HI).reduce((textValue, [en, hi]) => textValue.replace(new RegExp(en, 'g'), hi), value);
const areaLabels = (isHindi: boolean | undefined, areas: string[] = []) => (
  isHindi ? areas.map((area) => AREA_HI[area] || area).join(', ') : areas.map((area) => translateInline(currentLanguage(), area)).join(', ')
);
const tevaNote = (isHindi: boolean | undefined, value: string) => {
  if (!isHindi) return translateInline(currentLanguage(), value);
  if (value.startsWith('Affected by ')) return replacePlanetNames(value.replace('Affected by ', 'इनसे प्रभावित: ').replace('.', '।'));
  return TEVA_NOTE_HI[value] || replacePlanetNames(value);
};
const houseText = (isHindi: boolean | undefined, house: number | string) => `${labelText(isHindi, 'House', 'भाव')} ${house}`;
const planetInHouse = (isHindi: boolean | undefined, planet: string, house: number | string) => (
  isHindi ? `${planetLabel(isHindi, planet)} ${houseText(isHindi, house)} में` : `${planet} in House ${house}`
);
const localizedReading = (isHindi: boolean | undefined, item: any) => {
  if (!isHindi) return item.reading;
  return `${planetLabel(isHindi, item.planet)} ${houseText(isHindi, item.house)} में होने से इस जीवन क्षेत्र का महत्व बढ़ता है। लाल किताब में भाव को प्राथमिक और राशि को द्वितीयक माना जाता है।`;
};
const localizedRemedyReason = (isHindi: boolean | undefined, item: any) => {
  const status = item?.remedyEligibility?.status;
  if (!isHindi) return item?.remedyEligibility?.reason;
  if (status === 'CAUTION') {
    return `${planetLabel(isHindi, item.planet)} पक्का घर में है, इसलिए अपने आप मजबूत उपाय नहीं सुझाने चाहिए।`;
  }
  return `${planetLabel(isHindi, item.planet)} पक्का घर में नहीं है। यदि पीड़ा दिखे तो सुरक्षित सामान्य उपाय दिखाए जा सकते हैं।`;
};
const localizedMovementStatus = (isHindi: boolean | undefined, movement: any) => {
  if (!isHindi) return movement.yearlyStatus;
  if (movement.movedToDusthana) return 'इस वर्ष सावधानी की जरूरत';
  if (movement.movedToPakkaGhar) return 'इस वर्ष मजबूत सीधा परिणाम';
  return 'सामान्य वार्षिक परिणाम';
};
const severityColor = (severity?: string) => {
  if (severity === 'high') return 'error';
  if (severity === 'positive') return 'success';
  if (severity === 'medium') return 'warning';
  return 'default';
};
const localizedTevaReason = (isHindi: boolean | undefined, item: any) => {
  if (!isHindi) return item.reason;
  if (RIN_REASON_HI[item.key]) return RIN_REASON_HI[item.key];
  if (item.key === 'andhi_kundli') return 'दसवें भाव में शत्रु ग्रहों का संबंध दिख रहा है।';
  if (item.key === 'aadhi_andhi_kundli') return 'सूर्य चौथे भाव में और शनि सातवें भाव में है।';
  if (item.key === 'nabalig_teva') return 'केंद्र भाव खाली हैं या केवल शनि/राहु/केतु या अकेले बुध से प्रभावित हैं।';
  if (item.key === 'dharmi_teva') return 'गुरु और शनि का लाल किताब में रक्षण/धर्म से जुड़ा मजबूत संबंध दिख रहा है।';
  if (item.key?.includes('_soya_hua_grah')) return `${planetLabel(isHindi, item.planet)} पूर्ण फल देने में धीमा या दबा हुआ हो सकता है।`;
  if (item.key?.startsWith('house_')) return `${houseText(isHindi, item.house)} में कोई ग्रह स्थित नहीं है।`;
  if (item.key?.includes('_pakka_ghar')) return `${planetLabel(isHindi, item.planet)} अपने लाल किताब पक्का घर में स्थित है।`;
  if (item.planet1 && item.planet2) return `${planetLabel(isHindi, item.planet1)} और ${planetLabel(isHindi, item.planet2)} के बीच ${tevaTypeLabel(isHindi, item.type)} का संकेत है।`;
  if (item.planets?.length) return `${item.planets.map((p: string) => planetLabel(isHindi, p)).join(' + ')} ${houseText(isHindi, item.house)} में साथ स्थित हैं।`;
  return item.reason;
};
const localizedTevaInterpretation = (isHindi: boolean | undefined, item: any) => {
  if (!isHindi) return item.interpretation;
  if (RIN_INTERPRETATION_HI[item.key]) return RIN_INTERPRETATION_HI[item.key];
  if (item.key === 'andhi_kundli') return 'यह करियर, कर्म, अधिकार और जीवन दिशा में भ्रम या अस्थिरता का संकेत दे सकता है।';
  if (item.key === 'aadhi_andhi_kundli') return 'यह घर, विवाह और मानसिक शांति से जुड़े मामलों में देरी या आधे-अधूरे परिणाम दिखा सकता है।';
  if (item.key === 'nabalig_teva') return 'ऐसे टेवा को सावधानी से पढ़ना चाहिए; परिणाम उम्र और अनुभव के साथ परिपक्व हो सकते हैं।';
  if (item.key === 'dharmi_teva') return 'कठिन समय में बुजुर्ग, गुरु, अनुशासन या ईश कृपा से सहयोग मिल सकता है।';
  if (item.key?.includes('_soya_hua_grah')) return `${planetLabel(isHindi, item.planet)} स्वाभाविक रूप से पूर्ण परिणाम न दे और सोया हुआ ग्रह जैसा व्यवहार कर सकता है।`;
  if (item.key?.startsWith('house_')) return `${houseText(isHindi, item.house)} को पूर्ण फल देने से पहले सक्रियता या सहयोग की जरूरत हो सकती है।`;
  if (item.key?.includes('_pakka_ghar')) return 'यह ग्रह टेवा में अधिक स्पष्ट, स्थिर और महत्वपूर्ण हो जाता है।';
  if (item.planet1 && item.planet2) return 'यह दोनों ग्रहों के कारक क्षेत्रों में दबाव, संघर्ष या सावधानी की जरूरत दिखा सकता है।';
  if (item.planets?.length) return 'इसे ग्रहों के संयुक्त व्यवहार की तरह पढ़ें, अलग भौतिक ग्रह की तरह नहीं।';
  return item.interpretation;
};
const localizedTevaGuidance = (isHindi: boolean | undefined, item: any) => {
  if (!isHindi) return item.guidance;
  if (RIN_GUIDANCE_HI[item.key]) return RIN_GUIDANCE_HI[item.key];
  if (item.key?.includes('_soya_hua_grah')) return 'ग्रह को बिना पूरी कुंडली देखे सक्रिय न करें। पहले शुभ-अशुभ स्थिति जांचें।';
  if (item.key?.startsWith('house_')) return 'इसे हल्का संकेत मानें; अंतिम निर्णय दृष्टि, भाव स्वामी और जीवन संदर्भ से करें।';
  if (item.key?.includes('_pakka_ghar')) return 'इस ग्रह के प्राकृतिक कारकों को साफ, संतुलित और सम्मानजनक रखें।';
  if (item.planet1 && item.planet2) return 'संबंधित जीवन क्षेत्रों में जल्दबाजी, शॉर्टकट और अहंकार से बचें।';
  if (item.planets?.length) return 'इसे संयुक्त ग्रह व्यवहार के रूप में पढ़ें और डर आधारित निष्कर्ष न निकालें।';
  return item.guidance;
};
const localizedKismatInterpretation = (isHindi: boolean | undefined, kismat: any) => {
  if (!isHindi || !kismat?.planet) return kismat?.interpretation;
  return `${planetLabel(isHindi, kismat.planet)} लाल किताब स्कोरिंग के आधार पर किस्मत का ग्रह बनने का मजबूत उम्मीदवार है। यह ${areaLabels(isHindi, kismat.areas) || 'महत्वपूर्ण जीवन क्षेत्रों'} में सहयोग दे सकता है।`;
};
const originalSignText = (value: any) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  return value.name || value.sign || null;
};
const getPreferredVarshAge = (chartInfo: any) => (
  chartInfo?.usedAge || chartInfo?.runningAge || chartInfo?.completedAge || 1
);

const LalKitabSubtabs: React.FC<LalKitabSubtabsProps> = ({ chartId, dob, authToken, initialReport, isHindi }) => {
  const [activeTab, setActiveTab] = React.useState(0);
  const [loading, setLoading] = React.useState<Record<string, boolean>>({});
  const [data, setData] = React.useState<Record<string, any>>({});
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [selectedVarshAge, setSelectedVarshAge] = React.useState<number | ''>('');

  const tabs = [
    { label: labelText(isHindi, 'Janam Kundli Analysis', 'जन्म कुंडली विश्लेषण'), key: 'janam' },
    { label: labelText(isHindi, 'Lal Kitab Drishti', 'लाल किताब दृष्टि'), key: 'drishti' },
    { label: labelText(isHindi, 'Lal Kitab Varshphal', 'लाल किताब वर्षफल'), key: 'varshphal' },
    { label: labelText(isHindi, 'Dasha / Age Periods', 'दशा / आयु काल'), key: 'age' },
    { label: labelText(isHindi, 'Remedy Eligibility', 'उपाय पात्रता'), key: 'remedy' },
    { label: labelText(isHindi, '43-Day Remedy Plan', '43-दिन उपाय योजना'), key: 'plan43' },
    { label: labelText(isHindi, 'Advanced Teva', 'एडवांस्ड टेवा'), key: 'teva' }
  ];

  const headers = {
    'Content-Type': 'application/json',
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
  };

  const fetchDrishti = async () => {
    if (data.drishti) return;
    try {
      setLoading((p) => ({ ...p, drishti: true }));
      const response = await fetch(`${API_BASE_URL}/lal-kitab/drishti`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ chartId, language: currentLanguage() })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.message || 'Failed to load drishti');
      setData((p) => ({ ...p, drishti: payload.data }));
      setErrors((p) => ({ ...p, drishti: '' }));
    } catch (err: any) {
      setErrors((p) => ({ ...p, drishti: err.message || 'Unable to load drishti' }));
    } finally {
      setLoading((p) => ({ ...p, drishti: false }));
    }
  };

  const fetchVarshphal = async (targetAge?: number) => {
    if (data.varshphal && targetAge === undefined) return;
    try {
      setLoading((p) => ({ ...p, varshphal: true }));
      const response = await fetch(`${API_BASE_URL}/lal-kitab/varshphal`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          chartId,
          ...(dob ? { dob } : {}),
          ...(targetAge ? { targetAge } : {}),
          language: currentLanguage()
        })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.message || 'Failed to load varshphal');
      setData((p) => ({ ...p, varshphal: payload.data }));
      const preferredAge = getPreferredVarshAge(payload.data?.chartInfo);
      if (preferredAge) {
        setSelectedVarshAge(Number(preferredAge));
      }
      setErrors((p) => ({ ...p, varshphal: '' }));
    } catch (err: any) {
      setErrors((p) => ({ ...p, varshphal: err.message || 'Unable to load varshphal' }));
    } finally {
      setLoading((p) => ({ ...p, varshphal: false }));
    }
  };

  const fetchAgePeriods = async () => {
    if (data.age || !dob) return;
    try {
      setLoading((p) => ({ ...p, age: true }));
      const response = await fetch(`${API_BASE_URL}/lal-kitab/age-periods`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ chartId, dob, language: currentLanguage() })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.message || 'Failed to load age periods');
      setData((p) => ({ ...p, age: payload.data }));
      setErrors((p) => ({ ...p, age: '' }));
    } catch (err: any) {
      setErrors((p) => ({ ...p, age: err.message || 'Unable to load age periods' }));
    } finally {
      setLoading((p) => ({ ...p, age: false }));
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    const key = tabs[newValue].key;
    if (key === 'drishti') fetchDrishti();
    if (key === 'varshphal' && chartId) fetchVarshphal();
    if (key === 'age') fetchAgePeriods();
  };

  const loadVarshphalForAge = (age: number | '') => {
    if (age === '') return;
    const normalizedAge = Math.max(1, Math.round(Number(age)));
    setSelectedVarshAge(normalizedAge);
    fetchVarshphal(normalizedAge);
  };

  const tevaAnalysis = initialReport?.tevaAnalysis;
  const renderFindingCards = (title: string, hiTitle: string, items: any[], emptyText: string, hiEmptyText: string) => (
    <Grid size={12}>
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{labelText(isHindi, title, hiTitle)}</Typography>
          {items?.length ? (
            <Grid container spacing={2}>
              {items.map((item: any, idx: number) => (
                <Grid size={{ xs: 12, md: 6 }} key={item.key || `${title}-${idx}`}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      <Chip label={labelText(isHindi, item.title || item.type || item.artificialEffect || item.planet || title, item.hindiTitle || item.title || tevaTypeLabel(isHindi, item.type || item.artificialEffect) || item.planet || hiTitle)} size="small" color={severityColor(item.severity) as any} />
                      {item.house && <Chip label={houseText(isHindi, item.house)} size="small" variant="outlined" />}
                      {(item.type || item.artificialEffect) && <Chip label={tevaTypeLabel(isHindi, item.type || item.artificialEffect)} size="small" variant="outlined" />}
                    </Box>
                    {item.planets?.length ? (
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>{item.planets.map((p: string) => planetLabel(isHindi, p)).join(' + ')}</Typography>
                    ) : null}
                    {item.planet1 && item.planet2 ? (
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>{planetLabel(isHindi, item.planet1)} + {planetLabel(isHindi, item.planet2)}</Typography>
                    ) : null}
                    {item.reason && <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>{localizedTevaReason(isHindi, item)}</Typography>}
                    {item.interpretation && <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>{localizedTevaInterpretation(isHindi, item)}</Typography>}
                    {item.guidance && <Typography variant="caption" color="text.secondary">{localizedTevaGuidance(isHindi, item)}</Typography>}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">{labelText(isHindi, emptyText, hiEmptyText)}</Typography>
          )}
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box>
      <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        {tabs.map((tab) => (
          <Tab key={tab.key} label={tab.label} />
        ))}
      </Tabs>

      {activeTab === 0 && (
        <Grid container spacing={2}>
          {(initialReport?.planetWise || []).map((item: any) => (
            <Grid size={{ xs: 12, md: 6 }} key={`${item.planet}-${item.house}`}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Chip label={planetInHouse(isHindi, item.planet, item.house)} color="primary" size="small" />
                  <Chip label={item.isInPakkaGhar ? labelText(isHindi, 'Pakka Ghar', 'पक्का घर') : labelText(isHindi, 'Not Pakka Ghar', 'पक्का घर नहीं')} size="small" variant="outlined" />
                </Box>
                <Typography variant="body2" color="text.secondary">{localizedReading(isHindi, item)}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {labelText(isHindi, 'Lal Kitab Sign', 'लाल किताब राशि')}: {item.lkSign || 'N/A'} • {labelText(isHindi, 'House Lord', 'भाव स्वामी')}: {planetLabel(isHindi, item.lkSignLord) || 'N/A'}
                </Typography>
                {originalSignText(item.originalSign) && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {labelText(isHindi, 'Original Vedic Sign', 'मूल वैदिक राशि')}: {originalSignText(item.originalSign)} {labelText(isHindi, 'only for reference', 'केवल संदर्भ के लिए')}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {labelText(isHindi, 'Remedy Status', 'उपाय स्थिति')}: {statusLabel(isHindi, item.remedyEligibility?.status)} - {localizedRemedyReason(isHindi, item)}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {activeTab === 1 && (
        <Box>
          {loading.drishti && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          )}
          {errors.drishti && <Alert severity="error" sx={{ mb: 3 }}>{errors.drishti}</Alert>}
          {data.drishti && !loading.drishti && (
            <Grid container spacing={3}>
              <Grid size={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{labelText(isHindi, 'Normal Lal Kitab Drishti', 'सामान्य लाल किताब दृष्टि')}</Typography>
                    <Grid container spacing={2}>
                      {(data.drishti.normalAspects || []).map((aspect: any, idx: number) => (
                        <Grid size={{ xs: 12, md: 6 }} key={idx}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Chip label={`${aspect.fromHouse} → ${aspect.toHouse}`} variant="outlined" size="small" />
                              <Chip label={`${aspect.strength}%`} size="small" />
                            </Box>
                            <Typography variant="body2">
                              {planetLabel(isHindi, aspect.aspectingPlanet)} {aspect.hasTargetPlanet ? `→ ${aspect.targetPlanets.map((p: string) => planetLabel(isHindi, p)).join(', ')}` : labelText(isHindi, '(empty target house)', '(लक्षित भाव खाली)')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {aspect.hasTargetPlanet
                                ? labelText(
                                    isHindi,
                                    aspect.interpretation,
                                    `${planetLabel(isHindi, aspect.aspectingPlanet)} ${houseText(isHindi, aspect.fromHouse)} से ${houseText(isHindi, aspect.toHouse)} में स्थित ${aspect.targetPlanets.map((p: string) => planetLabel(isHindi, p)).join(', ')} को प्रभावित करता है।`
                                  )
                                : labelText(isHindi, aspect.interpretation, `${planetLabel(isHindi, aspect.aspectingPlanet)} ${houseText(isHindi, aspect.fromHouse)} से खाली ${houseText(isHindi, aspect.toHouse)} को देखता है। सक्रिय होने तक परिणाम शांत रह सकता है।`)}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{labelText(isHindi, 'Special Drishti Types', 'विशेष दृष्टि प्रकार')}</Typography>
                    <Grid container spacing={2}>
                      {(data.drishti.specialDrishti || []).map((drishti: any, idx: number) => (
                        <Grid size={{ xs: 12, md: 6 }} key={idx}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Chip label={drishti.drishtiName} size="small" color="primary" />
                              <Chip label={statusLabel(isHindi, drishti.nature)} size="small" variant="outlined" />
                            </Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              {planetLabel(isHindi, drishti.aspectingPlanet)} ({labelText(isHindi, 'H', 'भाव ')}{drishti.fromHouse}) → {drishti.targetPlanets.map((p: string) => planetLabel(isHindi, p)).join(', ')} ({labelText(isHindi, 'H', 'भाव ')}{drishti.toHouse})
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {labelText(isHindi, drishti.description, 'यह लाल किताब की विशेष दृष्टि है; परिणाम ग्रह, भाव और वर्षफल की स्थिति के अनुसार पढ़ें।')}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          {!loading.varshphal && !errors.varshphal && (
            <Alert severity="info" sx={{ mb: 3 }}>
              {labelText(
                isHindi,
                'Kundlistar will auto-generate Lal Kitab Varshphal from DOB and selected running age using progressed Lal Kitab houses.',
                'Kundlistar जन्म तिथि और चुनी गई चलती आयु से प्रोग्रेस्ड लाल किताब भावों के आधार पर लाल किताब वर्षफल अपने आप बनाएगा।'
              )}
            </Alert>
          )}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mr: 1 }}>
                {labelText(isHindi, 'Select Varshphal Age', 'वर्षफल आयु चुनें')}
              </Typography>
              <Tooltip title={labelText(isHindi, 'Previous age', 'पिछली आयु')}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => loadVarshphalForAge(Math.max(1, Number(selectedVarshAge || getPreferredVarshAge(data.varshphal?.chartInfo)) - 1))}
                    disabled={loading.varshphal}
                  >
                    <Remove fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <TextField
                label={labelText(isHindi, 'Used age', 'उपयोग आयु')}
                type="number"
                size="small"
                value={selectedVarshAge}
                onChange={(event) => {
                  const value = event.target.value;
                  const numericValue = Number(value);
                  setSelectedVarshAge(value === '' || !Number.isFinite(numericValue) ? '' : Math.max(1, Math.round(numericValue)));
                }}
                onBlur={() => loadVarshphalForAge(selectedVarshAge)}
                inputProps={{ min: 1 }}
                sx={{ width: 150 }}
              />
              <Tooltip title={labelText(isHindi, 'Next age', 'अगली आयु')}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => loadVarshphalForAge(Number(selectedVarshAge || getPreferredVarshAge(data.varshphal?.chartInfo)) + 1)}
                    disabled={loading.varshphal}
                  >
                    <Add fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<AutoAwesome />}
                onClick={() => loadVarshphalForAge(selectedVarshAge)}
                disabled={loading.varshphal || selectedVarshAge === ''}
              >
                {labelText(isHindi, 'Get Varshphal', 'वर्षफल देखें')}
              </Button>
              {data.varshphal?.chartInfo?.targetYear && (
                <Typography variant="caption" color="text.secondary">
                  {labelText(isHindi, 'Year', 'वर्ष')}: {data.varshphal.chartInfo.targetYear}
                </Typography>
              )}
            </Box>
          </Paper>
          {loading.varshphal && <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}
          {errors.varshphal && <Alert severity="error" sx={{ mb: 3 }}>{errors.varshphal}</Alert>}
          {data.varshphal && !loading.varshphal && (
            <Grid container spacing={3}>
              {data.varshphal.varshChart && (
                <Grid size={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        {labelText(isHindi, 'Lal Kitab Varshphal Chart • Fixed Khana Layout', 'लाल किताब वर्षफल चार्ट • स्थिर खाना लेआउट')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        {data.varshphal.chartInfo?.targetYear && (
                          <Chip label={`${labelText(isHindi, 'Year', 'वर्ष')}: ${data.varshphal.chartInfo.targetYear}`} size="small" />
                        )}
                        {data.varshphal.chartInfo?.completedAge && (
                          <Chip label={`${labelText(isHindi, 'Completed Age', 'पूर्ण आयु')}: ${data.varshphal.chartInfo.completedAge}`} size="small" variant="outlined" />
                        )}
                        {data.varshphal.chartInfo?.runningAge && (
                          <Chip label={`${labelText(isHindi, 'Running Age', 'चलती आयु')}: ${data.varshphal.chartInfo.runningAge}`} size="small" variant="outlined" />
                        )}
                        {data.varshphal.chartInfo?.usedAge && (
                          <Chip label={`${labelText(isHindi, 'Used Age', 'उपयोग आयु')}: ${data.varshphal.chartInfo.usedAge}`} size="small" color="primary" variant="outlined" />
                        )}
                        {data.varshphal.chartInfo?.ageMode && (
                          <Chip label={`${labelText(isHindi, 'Age Mode', 'आयु मोड')}: ${data.varshphal.chartInfo.ageMode}`} size="small" color="primary" variant="outlined" />
                        )}
                        {data.varshphal.chartInfo?.validFrom && data.varshphal.chartInfo?.validTo && (
                          <Chip label={`${data.varshphal.chartInfo.validFrom} - ${data.varshphal.chartInfo.validTo}`} size="small" color="success" variant="outlined" />
                        )}
                        {data.varshphal.generatedAutomatically !== undefined && (
                          <Chip
                            label={data.varshphal.generatedAutomatically
                              ? labelText(isHindi, 'Auto Generated', 'स्वचालित रूप से निर्मित')
                              : labelText(isHindi, 'Using Selected Varsh Chart', 'चयनित वर्ष चार्ट उपयोग में')}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      {data.varshphal.method && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {labelText(isHindi, data.varshphal.method, data.varshphal.method)}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <LalKitabChart
                          chartData={data.varshphal.varshChart}
                          size={340}
                          showHouseLabels
                          showPlanetTable
                          isHindi={isHindi}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              <Grid size={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{labelText(isHindi, '10th House — Deceptive Planet Analysis', '10वां भाव — धोखा/अनिश्चित ग्रह विश्लेषण')}</Typography>
                    {data.varshphal.chartInfo && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        <Chip label={`${labelText(isHindi, 'Year', 'वर्ष')}: ${data.varshphal.chartInfo.targetYear}`} size="small" />
                        <Chip label={`${labelText(isHindi, 'Completed Age', 'पूर्ण आयु')}: ${data.varshphal.chartInfo.completedAge}`} size="small" variant="outlined" />
                        {data.varshphal.chartInfo.usedAge && (
                          <Chip label={`${labelText(isHindi, 'Used Age', 'उपयोग आयु')}: ${data.varshphal.chartInfo.usedAge}`} size="small" color="primary" variant="outlined" />
                        )}
                        {data.varshphal.chartInfo.ageMode && (
                          <Chip label={`${labelText(isHindi, 'Age Mode', 'आयु मोड')}: ${data.varshphal.chartInfo.ageMode}`} size="small" color="primary" variant="outlined" />
                        )}
                      </Box>
                    )}
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {labelText(isHindi, data.varshphal.tenthHouseDeception?.interpretation, 'इस वर्ष करियर, कर्म, प्रतिष्ठा और जिम्मेदारी से जुड़े निर्णय सावधानी से लेने चाहिए।')}
                    </Typography>
                    {(data.varshphal.tenthHouseDeception?.reasons || []).map((reason: string, idx: number) => (
                      <Typography key={idx} variant="body2" color="text.secondary">• {reason}</Typography>
                    ))}
                    {!data.varshphal.tenthHouseDeception?.active && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {labelText(isHindi, data.varshphal.tenthHouseDeception?.title, '10वें भाव में कोई ग्रह नहीं')}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {data.varshphal.hasVarshChart && (data.varshphal.birthVarshComparison || []).length > 0 && (
                <Grid size={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{labelText(isHindi, 'Birth → Varshphal Planet Movement', 'जन्म → वर्षफल ग्रह गति')}</Typography>
                      <Grid container spacing={2}>
                        {(data.varshphal.birthVarshComparison || []).map((movement: any, idx: number) => (
                          <Grid size={{ xs: 12, md: 6 }} key={idx}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{planetLabel(isHindi, movement.planet)}: {movement.movement}</Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                {labelText(isHindi, 'Current Varsh House', 'वर्तमान वर्ष भाव')}: {movement.varshHouse} • {labelText(isHindi, 'Lal Kitab Sign', 'लाल किताब राशि')}: {movement.lkSign || 'N/A'} • {labelText(isHindi, 'House Lord', 'भाव स्वामी')}: {planetLabel(isHindi, movement.lkSignLord) || 'N/A'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">{localizedMovementStatus(isHindi, movement)}</Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                {labelText(isHindi, movement.interpretation, `${planetLabel(isHindi, movement.planet)} जन्म भाव ${movement.birthHouse} से वर्षफल भाव ${movement.varshHouse} में जाता है।`)}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {(data.varshphal.varshRemedyNeed || []).length > 0 && (
                <Grid size={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{labelText(isHindi, 'Varshphal Remedy Need', 'वर्षफल उपाय आवश्यकता')}</Typography>
                      <Grid container spacing={2}>
                        {(data.varshphal.varshRemedyNeed || []).map((item: any) => (
                          <Grid size={{ xs: 12, md: 6 }} key={`${item.planet}-${item.house}-varsh-remedy`}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{planetInHouse(isHindi, item.planet, item.house)}</Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                {labelText(isHindi, 'Lal Kitab Sign', 'लाल किताब राशि')}: {item.lkSign || 'N/A'} • {labelText(isHindi, 'House Lord', 'भाव स्वामी')}: {planetLabel(isHindi, item.lkSignLord) || 'N/A'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.needsTemporaryRemedy
                                  ? labelText(isHindi, '43-day temporary remedy can be considered.', '43-दिन का अस्थायी सुरक्षित उपाय विचार किया जा सकता है।')
                                  : labelText(isHindi, 'Conduct-based guidance only.', 'केवल आचरण-आधारित मार्गदर्शन।')}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </Box>
      )}

      {activeTab === 3 && (
        <Box>
          {!dob && <Alert severity="info" sx={{ mb: 3 }}>{labelText(isHindi, 'Date of birth is required for age period analysis.', 'आयु काल विश्लेषण के लिए जन्म तिथि जरूरी है।')}</Alert>}
          {loading.age && <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}
          {errors.age && <Alert severity="error" sx={{ mb: 3 }}>{errors.age}</Alert>}
          {data.age && !loading.age && (
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{labelText(isHindi, 'Current Age', 'वर्तमान आयु')}: {data.age.currentAge}</Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  {labelText(isHindi, data.age.note, 'यह लाल किताब टाइमिंग लेयर वर्तमान आयु, आगामी आयु विंडो और वर्षफल भाव गति को मिलाकर देखती है।')}
                </Typography>
                <Grid container spacing={2}>
                  {(data.age.activeAgePlanets || []).map((period: any, idx: number) => (
                    <Grid size={{ xs: 12, md: 6 }} key={idx}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{planetLabel(isHindi, period.planet)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {labelText(isHindi, 'Age', 'आयु')} {period.range?.start}-{period.range?.end} | {labelText(isHindi, 'Birth H', 'जन्म भाव')}: {period.birthHouse || 'N/A'} | {labelText(isHindi, 'Varsh H', 'वर्ष भाव')}: {period.varshHouse || 'N/A'}
                        </Typography>
                        {period.focus && <Typography variant="caption" display="block" color="text.secondary">{labelText(isHindi, period.focus, 'इस ग्रह से जुड़े विषय इस अवधि में सक्रिय रहते हैं।')}</Typography>}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                {(data.age.upcomingAgePlanets || []).length > 0 && (
                  <>
                    <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 2 }}>{labelText(isHindi, 'Upcoming Age Periods', 'आगामी आयु काल')}</Typography>
                    <Grid container spacing={2}>
                      {(data.age.upcomingAgePlanets || []).map((period: any, idx: number) => (
                        <Grid size={{ xs: 12, md: 6 }} key={`${period.planet}-upcoming-${idx}`}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{planetLabel(isHindi, period.planet)}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {labelText(isHindi, 'Age', 'आयु')} {period.range?.start}-{period.range?.end} | {labelText(isHindi, 'Peak', 'चरम')}: {period.range?.peak}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {activeTab === 4 && (
        <Grid container spacing={2}>
          {(initialReport?.planetWise || []).map((item: any) => (
            <Grid size={{ xs: 12, md: 6 }} key={`${item.planet}-remedy`}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography fontWeight={600}>{planetLabel(isHindi, item.planet)} - {statusLabel(isHindi, item.remedyEligibility?.status)}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>{localizedRemedyReason(isHindi, item)}</Typography>
                {item.remedyEligibility?.safeAlternative && (
                  <Typography variant="body2" sx={{ mt: 0.75 }}>
                    {labelText(isHindi, item.remedyEligibility.safeAlternative, 'आचरण, अनुशासन, दान, संबंधित पारिवारिक सम्मान और आत्मचिंतन को प्राथमिकता दें।')}
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {activeTab === 5 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{labelText(isHindi, '43-Day Remedy Plan', '43-दिन उपाय योजना')}</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>{labelText(isHindi, initialReport?.remedyPlan43Days?.note, 'लाल किताब में कई उपायों में निरंतरता का महत्व है। उपाय सरल, सुरक्षित और आचरण-आधारित रखें।')}</Typography>
            {(initialReport?.remedyPlan43Days?.steps || []).map((step: any) => (
              <Paper key={`${step.planet}-${step.house}`} variant="outlined" sx={{ p: 2, mb: 1 }}>
                <Typography fontWeight={600}>{planetLabel(isHindi, step.planet)} - {labelText(isHindi, step.duration, '43 दिन')}</Typography>
                <Typography variant="body2" color="text.secondary">{labelText(isHindi, step.guidance, 'इस ग्रह से जुड़े सुरक्षित आचरण और अनुशासन का पालन करें।')}</Typography>
              </Paper>
            ))}
          </CardContent>
        </Card>
      )}

      {activeTab === 6 && (
        <Grid container spacing={3}>
          <Grid size={12}>
            <Alert severity="info" sx={{ mb: 0 }}>
              {labelText(
                isHindi,
                tevaAnalysis?.disclaimer || 'This is rule-based Lal Kitab guidance. Use it for self-reflection, not fear-based decisions.',
                'यह नियम-आधारित लाल किताब मार्गदर्शन है। इसे आत्मचिंतन के लिए उपयोग करें, डर आधारित निर्णयों के लिए नहीं।'
              )}
            </Alert>
          </Grid>

          <Grid size={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{labelText(isHindi, 'Teva Summary', 'टेवा सारांश')}</Typography>
                <Grid container spacing={2}>
                  {[
                    [labelText(isHindi, 'Rins', 'ऋण'), tevaAnalysis?.summary?.totalRins || 0],
                    [labelText(isHindi, 'Teva Types', 'टेवा प्रकार'), tevaAnalysis?.summary?.totalTevaTypes || 0],
                    [labelText(isHindi, 'Takkar', 'टक्कर'), tevaAnalysis?.summary?.totalTakkar || 0],
                    [labelText(isHindi, 'Soya Grah', 'सोया ग्रह'), tevaAnalysis?.summary?.totalSoyaGrah || 0],
                    [labelText(isHindi, 'Pakka Ghar', 'पक्का घर'), tevaAnalysis?.summary?.totalPakkaGhar || 0],
                    [labelText(isHindi, 'Banawati Grah', 'बनावटी ग्रह'), tevaAnalysis?.summary?.totalBanawatiGrah || 0]
                  ].map(([label, value]) => (
                    <Grid size={{ xs: 6, md: 2 }} key={label}>
                      <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>{value}</Typography>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                {tevaAnalysis?.kismatKaGrah?.planet && (
                  <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      <Chip label={labelText(isHindi, 'Kismat Ka Grah', 'किस्मत का ग्रह')} color="success" size="small" />
                      <Chip label={planetLabel(isHindi, tevaAnalysis.kismatKaGrah.planet)} size="small" />
                      <Chip label={`${labelText(isHindi, 'Confidence', 'विश्वास')}: ${statusLabel(isHindi, tevaAnalysis.kismatKaGrah.confidence)}`} size="small" variant="outlined" />
                    </Box>
                    <Typography variant="body2" color="text.secondary">{localizedKismatInterpretation(isHindi, tevaAnalysis.kismatKaGrah)}</Typography>
                  </Paper>
                )}
              </CardContent>
            </Card>
          </Grid>

          {renderFindingCards('Rins / Debts', 'ऋण / कर्ज', tevaAnalysis?.rins || [], 'No major Lal Kitab Rin detected in this rule set.', 'इस नियम सेट में कोई प्रमुख लाल किताब ऋण नहीं मिला।')}
          {renderFindingCards('Special Teva Types', 'विशेष टेवा प्रकार', tevaAnalysis?.tevaTypes?.detected || [], 'Normal Teva. No special condition detected.', 'सामान्य टेवा। कोई विशेष स्थिति नहीं मिली।')}
          {renderFindingCards('Takkar Ke Grah', 'टक्कर के ग्रह', tevaAnalysis?.takkarKeGrah || [], 'No major Takkar combination detected.', 'कोई प्रमुख टक्कर योग नहीं मिला।')}
          {renderFindingCards('Soya Hua Grah', 'सोया हुआ ग्रह', tevaAnalysis?.soyaHuaGrah || [], 'No Soya Hua Grah detected.', 'कोई सोया हुआ ग्रह नहीं मिला।')}
          {renderFindingCards('Pakka Ghar', 'पक्का घर', tevaAnalysis?.pakkaGhar || [], 'No planet is in Pakka Ghar.', 'कोई ग्रह पक्का घर में नहीं है।')}
          {renderFindingCards('Banawati Grah', 'बनावटी ग्रह', tevaAnalysis?.banawatiGrah || [], 'No Banawati Grah combination detected.', 'कोई बनावटी ग्रह संयोजन नहीं मिला।')}

          <Grid size={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{labelText(isHindi, 'Planet Condition Summary', 'ग्रह स्थिति सारांश')}</Typography>
                <Grid container spacing={2}>
                  {(tevaAnalysis?.planetConditionSummary || []).map((item: any) => (
                    <Grid size={{ xs: 12, md: 6 }} key={`${item.planet}-condition`}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{planetInHouse(isHindi, item.planet, item.house)}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', my: 1 }}>
                          {(item.labels || []).map((label: string) => <Chip key={label} label={tevaLabel(isHindi, label)} size="small" variant="outlined" />)}
                        </Box>
                        {(item.notes || []).map((note: string) => (
                          <Typography key={note} variant="caption" display="block" color="text.secondary">{tevaNote(isHindi, note)}</Typography>
                        ))}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default LalKitabSubtabs;
